import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../../lib/api";
import { API } from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail } from "lucide-react";

const STATUSES = ["pending_payment", "paid", "processing", "shipped", "delivered", "cancelled"];

const AdminOrderDetail = () => {
  const { id } = useParams();
  const { lang } = useI18n();
  const [order, setOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [emailing, setEmailing] = useState(false);

  const load = () => apiClient.get(`/admin/orders/${id}`).then((r) => { setOrder(r.data); setNewStatus(r.data.status); });
  useEffect(() => { load(); }, [id]);

  if (!order) return <div className="text-ink-muted">Loading…</div>;

  const updateStatus = async () => {
    try {
      await apiClient.put(`/admin/orders/${id}/status?status_value=${newStatus}`);
      toast.success(lang === "nl" ? "Status bijgewerkt" : "Status updated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const downloadInvoice = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API}/orders/${id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${order.invoice_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Failed");
    }
  };

  const resendInvoice = async () => {
    setEmailing(true);
    try {
      await apiClient.post(`/admin/orders/${id}/invoice/email`);
      toast.success(lang === "nl" ? "Factuur opnieuw verzonden" : "Invoice re-sent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setEmailing(false); }
  };

  const paid = order.payment_status === "paid";

  return (
    <div data-testid="admin-order-detail-page">
      <Link to="/admin/orders" className="font-label text-ink-muted hover:text-ink flex items-center gap-1.5 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> {lang === "nl" ? "Bestellingen" : "Orders"}</Link>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-label text-ink-muted">Order</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{order.order_id}</h1>
          <div className="text-xs text-ink-muted mt-1">
            {order.created_at?.slice(0, 19).replace("T", " ")} · tracking <strong>{order.tracking_code}</strong>
            {order.invoice_number && <> · {lang === "nl" ? "Factuur" : "Invoice"} <strong>{order.invoice_number}</strong></>}
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          {paid && (
            <>
              <button onClick={downloadInvoice} data-testid="admin-download-invoice" className="btn-secondary text-xs py-2.5 px-3"><Download className="w-3.5 h-3.5" /> {lang === "nl" ? "Factuur" : "Invoice"}</button>
              <button onClick={resendInvoice} disabled={emailing} data-testid="admin-resend-invoice" className="btn-secondary text-xs py-2.5 px-3"><Mail className="w-3.5 h-3.5" /> {emailing ? "…" : (lang === "nl" ? "Email factuur" : "Email invoice")}</button>
            </>
          )}
          <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="border border-black/15 bg-white px-3 py-2 text-sm" data-testid="order-status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={updateStatus} className="btn-primary text-sm" data-testid="order-status-save">{lang === "nl" ? "Opslaan" : "Save"}</button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <div className="border border-black/10 bg-white p-6">
            <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Items" : "Line items"}</div>
            {order.lines.map((l, i) => (
              <div key={i} className="py-3 flex items-center gap-4 border-b border-black/5 last:border-0">
                <div className="w-12 h-14 bg-secondary-warm overflow-hidden shrink-0"><img src={l.image} alt="" className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display">{lang === "nl" ? l.name_nl : l.name_en}</div>
                  <div className="text-xs text-ink-muted">{l.sku} · {l.qty} × €{l.unit_price.toFixed(2)} · BTW {Number(l.vat_rate ?? 21).toString()}%</div>
                </div>
                <div className="font-display">€{l.line_total.toFixed(2)}</div>
              </div>
            ))}
          </div>

          <div className="border border-black/10 bg-white p-6">
            <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Tracking" : "Tracking events"}</div>
            <div className="space-y-2">
              {(order.tracking_events || []).map((e, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-black/5 py-2 last:border-0">
                  <span>{e.status}</span>
                  <span className="text-ink-muted text-xs">{e.timestamp?.slice(0, 19).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-black/10 bg-white p-6">
            <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Klant" : "Customer"}</div>
            <div>{order.shipping?.full_name}</div>
            <div className="text-sm text-ink-muted">{order.email}</div>
            <div className="text-sm text-ink-muted">{order.shipping?.phone}</div>
            <div className="mt-3 text-sm">
              {order.shipping?.street} {order.shipping?.house_number}<br />
              {order.shipping?.postal_code} {order.shipping?.city}<br />
              {order.shipping?.country}
            </div>
          </div>

          <div className="border border-black/10 bg-white p-6 space-y-1.5 text-sm">
            <div className="font-label text-ink-muted mb-2">{lang === "nl" ? "Totalen" : "Totals"}</div>
            <Row k={lang === "nl" ? "Subtotaal (incl. BTW)" : "Subtotal (incl. VAT)"} v={`€${order.subtotal.toFixed(2)}`} />
            <Row k={lang === "nl" ? "Verzending" : "Shipping"} v={`€${order.shipping_cost.toFixed(2)}`} />
            {order.discount_amount > 0 && <Row k={`${lang === "nl" ? "Korting" : "Discount"} (${order.discount_code})`} v={`−€${order.discount_amount.toFixed(2)}`} />}
            {(order.vat_breakdown || []).map((b) => (
              <Row key={b.rate} k={`${lang === "nl" ? "BTW" : "VAT"} ${b.rate}%`} v={`€${b.vat.toFixed(2)}`} muted />
            ))}
            {(!order.vat_breakdown || order.vat_breakdown.length === 0) && (
              <Row k={lang === "nl" ? "BTW (incl.)" : "VAT (incl.)"} v={`€${(order.vat_amount || 0).toFixed(2)}`} muted />
            )}
            <Row k={lang === "nl" ? "Totaal excl. BTW" : "Total ex. VAT"} v={`€${(order.total_net ?? (order.grand_total - (order.vat_amount || 0))).toFixed(2)}`} muted />
            <div className="border-t border-black/10 pt-2 mt-2 font-display flex justify-between"><span>{lang === "nl" ? "Totaal" : "Total"}</span><span>€{order.grand_total.toFixed(2)}</span></div>
            <div className="pt-3 text-xs uppercase font-label text-ink-muted">Payment: {order.payment_method}</div>
            <div className="text-xs uppercase font-label text-ink-muted">Carrier: {order.carrier}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v, muted }) => <div className={`flex justify-between ${muted ? "text-ink-muted text-xs" : ""}`}><span>{k}</span><span>{v}</span></div>;

export default AdminOrderDetail;
